import React, { useEffect, useRef } from 'react';

/**
 * GroupMatchProgress - Real-time progress visualization for group tutor matching
 *
 * Industrial/Utilitarian aesthetic - mission control dashboard feel
 * Shows live updates as the AI evaluates each tutor-student combination
 */
const GroupMatchProgress = ({
  progress = 0,
  currentTutor = null,
  currentStudent = null,
  completedTutors = [],
  totalTutors = 0,
  totalStudents = 0,
  groupSize = 0
}) => {
  const completedListRef = useRef(null);

  // Auto-scroll completed tutors list
  useEffect(() => {
    if (completedListRef.current) {
      completedListRef.current.scrollTop = completedListRef.current.scrollHeight;
    }
  }, [completedTutors]);

  const getScoreColor = (score) => {
    if (score >= 85) return '#10b981'; // green
    if (score >= 70) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  const getScoreClass = (score) => {
    if (score >= 85) return 'score-high';
    if (score >= 70) return 'score-medium';
    return 'score-low';
  };

  // Sort completed tutors by score (highest first)
  const sortedTutors = [...completedTutors].sort((a, b) => b.group_score - a.group_score);

  return (
    <div className="group-match-progress">
      {/* Header */}
      <div className="gmp-header">
        <div className="gmp-title">
          <span className="gmp-icon">&#9881;</span>
          <span>GROUP TUTOR ANALYSIS</span>
        </div>
        <div className="gmp-meta">
          <span className="gmp-meta-item">{groupSize} students</span>
          <span className="gmp-meta-divider">|</span>
          <span className="gmp-meta-item">{totalTutors} tutors</span>
        </div>
      </div>

      {/* Main Progress Bar */}
      <div className="gmp-progress-section">
        <div className="gmp-progress-header">
          <span className="gmp-progress-label">PROGRESS</span>
          <span className="gmp-progress-value">{progress}%</span>
        </div>
        <div className="gmp-progress-bar">
          <div
            className="gmp-progress-fill"
            style={{ width: `${progress}%` }}
          >
            <div className="gmp-progress-glow"></div>
          </div>
        </div>
        <div className="gmp-progress-stats">
          <span>{completedTutors.length} of {totalTutors} tutors evaluated</span>
        </div>
      </div>

      {/* Current Operation */}
      <div className="gmp-current-section">
        <div className="gmp-pipeline">
          {/* Student Node */}
          <div className={`gmp-pipeline-node ${currentStudent ? 'active' : ''}`}>
            <div className="gmp-node-icon">&#128100;</div>
            <div className="gmp-node-label">STUDENT</div>
            <div className="gmp-node-value">
              {currentStudent ? (
                <>
                  <span className="gmp-node-primary">{currentStudent.grade}</span>
                  <span className="gmp-node-secondary">{currentStudent.subjects?.split(',')[0]}</span>
                </>
              ) : (
                <span className="gmp-node-waiting">...</span>
              )}
            </div>
          </div>

          {/* Arrow */}
          <div className="gmp-pipeline-arrow">
            <div className="gmp-arrow-line"></div>
            <div className="gmp-arrow-head">&#9654;</div>
          </div>

          {/* AI Node */}
          <div className={`gmp-pipeline-node gmp-ai-node ${currentTutor ? 'processing' : ''}`}>
            <div className="gmp-node-icon">&#9881;</div>
            <div className="gmp-node-label">AI ANALYSIS</div>
            <div className="gmp-node-value">
              {currentTutor ? (
                <span className="gmp-processing-text">Evaluating...</span>
              ) : (
                <span className="gmp-node-waiting">Idle</span>
              )}
            </div>
          </div>

          {/* Arrow */}
          <div className="gmp-pipeline-arrow">
            <div className="gmp-arrow-line"></div>
            <div className="gmp-arrow-head">&#9654;</div>
          </div>

          {/* Tutor Node */}
          <div className={`gmp-pipeline-node ${currentTutor ? 'active' : ''}`}>
            <div className="gmp-node-icon">&#127891;</div>
            <div className="gmp-node-label">TUTOR</div>
            <div className="gmp-node-value">
              {currentTutor ? (
                <span className="gmp-node-primary">{currentTutor.name}</span>
              ) : (
                <span className="gmp-node-waiting">...</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Completed Tutors Feed */}
      {completedTutors.length > 0 && (
        <div className="gmp-completed-section">
          <div className="gmp-completed-header">
            <span className="gmp-section-label">EVALUATED TUTORS</span>
            <span className="gmp-section-count">{completedTutors.length}</span>
          </div>
          <div className="gmp-completed-list" ref={completedListRef}>
            {sortedTutors.map((tutor, index) => (
              <div
                key={tutor.tutor_id || index}
                className={`gmp-tutor-row ${getScoreClass(tutor.group_score)}`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="gmp-tutor-rank">#{index + 1}</div>
                <div className="gmp-tutor-info">
                  <span className="gmp-tutor-name">{tutor.tutor_name}</span>
                </div>
                <div className="gmp-tutor-score">
                  <div className="gmp-score-bar-bg">
                    <div
                      className="gmp-score-bar-fill"
                      style={{
                        width: `${tutor.group_score}%`,
                        backgroundColor: getScoreColor(tutor.group_score)
                      }}
                    ></div>
                  </div>
                  <span
                    className="gmp-score-value"
                    style={{ color: getScoreColor(tutor.group_score) }}
                  >
                    {tutor.group_score}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Footer */}
      <div className="gmp-footer">
        <div className="gmp-status-indicator">
          <span className="gmp-status-dot"></span>
          <span className="gmp-status-text">
            {currentTutor ? 'Processing...' : 'Waiting...'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GroupMatchProgress;
